import React, { createContext, useState, useEffect, useContext } from 'react';
import { getBaseUrls, getBaseUrlCategories, getBaseUrlTypesByCategory } from '../apis/contactLinkBaseUrlApi';
import { useError } from './ErrorContext';

export const ContactLinkBaseUrlContext = createContext();

export const ContactLinkBaseUrlProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [baseUrls, setBaseUrls] = useState([]);
  const [types, setTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get error context for global error handling
  const { addError, handleApiCall } = useError();

  // ✅ SAFE FIX: Add refs to prevent infinite loops
  const hasInitializedRef = React.useRef(false);
  const isLoadingRef = React.useRef(false);

  // ✅ ENHANCED CACHE SYSTEM
  const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes (increased from 5)
  const CACHE_VERSION = 'v2'; // Version to handle cache invalidation

  const getCacheKey = (type) => `contact-link-${type}-${CACHE_VERSION}`;
  const getTimestampKey = (type) => `contact-link-${type}-timestamp-${CACHE_VERSION}`;

  // ✅ ENHANCED: Better cache checking with error handling and version control
  const getCachedData = (type) => {
    try {
      const cacheKey = getCacheKey(type);
      const timestampKey = getTimestampKey(type);

      console.log(`🔍 Checking cache for ${type}...`);

      const cached = localStorage.getItem(cacheKey);
      const timestamp = localStorage.getItem(timestampKey);

      if (!cached || !timestamp) {
        console.log(`📦 No cache found for ${type}`);
        return null;
      }

      const parsedTimestamp = parseInt(timestamp, 10);
      if (isNaN(parsedTimestamp)) {
        console.log(`📦 Invalid timestamp for ${type}, clearing cache`);
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(timestampKey);
        return null;
      }

      const age = Date.now() - parsedTimestamp;
      console.log(`📦 Cache age for ${type}: ${Math.round(age / 1000)}s (max: ${CACHE_DURATION / 1000}s)`);

      if (age < CACHE_DURATION) {
        const parsed = JSON.parse(cached);
        console.log(`📦 Using cached ${type} data (${Math.round(age / 1000)}s old)`);
        return parsed;
      } else {
        console.log(`📦 Cache expired for ${type}, removing`);
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(timestampKey);
        return null;
      }
    } catch (error) {
      console.error(`❌ Error reading cached ${type}:`, error);
      // Clear corrupted cache
      try {
        localStorage.removeItem(getCacheKey(type));
        localStorage.removeItem(getTimestampKey(type));
      } catch (e) {
        console.error('Error clearing corrupted cache:', e);
      }
      return null;
    }
  };

  // ✅ ENHANCED: Better cache setting with error handling
  const setCachedData = (type, data) => {
    try {
      const cacheKey = getCacheKey(type);
      const timestampKey = getTimestampKey(type);

      localStorage.setItem(cacheKey, JSON.stringify(data));
      localStorage.setItem(timestampKey, Date.now().toString());
      console.log(`💾 Cached ${type} data successfully`);
    } catch (error) {
      console.error(`❌ Error caching ${type}:`, error);
      // If localStorage is full, try to clear old cache
      if (error.name === 'QuotaExceededError') {
        console.log('🗑️ Clearing old cache due to storage limit');
        clearAllCache();
      }
    }
  };

  // ✅ ENHANCED: Request deduplication for API calls
  const pendingRequests = React.useRef(new Map());

  const getBaseUrlTypesWithDeduplication = async (category) => {
    // Check if request is already in progress
    if (pendingRequests.current.has(category)) {
      console.log(`🔄 Request already in progress for ${category}, waiting...`);
      return await pendingRequests.current.get(category);
    }

    // Create new request promise
    const requestPromise = handleApiCall(
      () => getBaseUrlTypesByCategory(category),
      {
        source: 'contact-link-types',
        retryable: true,
        autoDismiss: true
      }
    );

    // Store the promise
    pendingRequests.current.set(category, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up the pending request
      pendingRequests.current.delete(category);
    }
  };

  // ✅ FIXED: Enhanced parallel fetching with better error handling
  const fetchTypesForCategories = async (categoriesList) => {
    // ✅ CRITICAL: Check cache first
    const cachedTypes = getCachedData('types');
    if (cachedTypes && Object.keys(cachedTypes).length > 0) {
      console.log(`📦 Using cached types for ${Object.keys(cachedTypes).length} categories`);
      return cachedTypes;
    }

    console.log(`🔄 Fetching types for ${categoriesList.length} categories in parallel`);

    // ✅ PARALLEL calls with deduplication
    const typePromises = categoriesList.map(async (category) => {
      try {
        const typesForCategory = await getBaseUrlTypesWithDeduplication(category);
        return [category, typesForCategory];
      } catch (error) {
        console.error(`❌ Error fetching types for category ${category}:`, error);
        return [category, []];
      }
    });

    // Wait for all requests to complete
    const typeResults = await Promise.all(typePromises);
    const fetchedTypes = Object.fromEntries(typeResults);

    // ✅ Cache the results
    setCachedData('types', fetchedTypes);

    console.log(`✅ Fetched types for ${categoriesList.length} categories`);
    return fetchedTypes;
  };

  useEffect(() => {
    const fetchData = async () => {
      // ✅ SAFE: Prevent multiple loads and infinite loops
      if (hasInitializedRef.current || isLoadingRef.current) {
        return;
      }

      try {
        // ✅ SAFE: Mark as loading immediately
        isLoadingRef.current = true;
        hasInitializedRef.current = true;

        setLoading(true);
        setError(null);

        // ✅ PRESERVED: All your existing cache logic stays exactly the same
        const cachedBaseUrls = getCachedData('baseUrls');
        const cachedCategories = getCachedData('categories');
        const cachedTypes = getCachedData('types');

        let fetchedBaseUrls, fetchedCategories;

        // ✅ PRESERVED: Your cache checking logic unchanged
        if (cachedBaseUrls && cachedCategories) {
          console.log('📦 Using cached base URLs and categories');
          fetchedBaseUrls = cachedBaseUrls;
          fetchedCategories = cachedCategories;
          setBaseUrls(fetchedBaseUrls);
          setCategories(fetchedCategories);

          if (cachedTypes && Object.keys(cachedTypes).length > 0) {
            console.log('📦 Using cached types data as well');
            setTypes(cachedTypes);
            setLoading(false);
            return; // ✅ PRESERVED: Early return logic unchanged
          }
        } else {
          console.log('🔄 Fetching base URLs and categories from API');
          // ✅ PRESERVED: Your API call logic - using the functions directly instead of from deps
          [fetchedBaseUrls, fetchedCategories] = await Promise.all([
            handleApiCall(
              () => getBaseUrls(),
              {
                source: 'contact-link-base-urls',
                retryable: true,
                autoDismiss: true
              }
            ),
            handleApiCall(
              () => getBaseUrlCategories(),
              {
                source: 'contact-link-categories',
                retryable: true,
                autoDismiss: true
              }
            )
          ]);

          // ✅ PRESERVED: Your caching logic unchanged
          setCachedData('baseUrls', fetchedBaseUrls);
          setCachedData('categories', fetchedCategories);

          setBaseUrls(fetchedBaseUrls);
          setCategories(fetchedCategories);
        }

        // ✅ PRESERVED: Your types fetching logic unchanged
        const fetchedTypes = await fetchTypesForCategories(fetchedCategories);
        setTypes(fetchedTypes);

      } catch (error) {
        console.error('❌ Error fetching contact link base URL data:', error);

        // ✅ PRESERVED: Your error handling logic unchanged
        if (error.message?.includes('validation') ||
          error.message?.includes('permission') ||
          error.message?.includes('session') ||
          error.response?.status === 401 ||
          error.response?.status === 403) {
          addError(error, {
            source: 'contact-link-base-urls',
            type: 'data-loading',
            retryable: true,
            autoDismiss: false
          });
        }

        setError(error.message || 'Failed to load contact link data');
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    };

    fetchData();
  }, []); // ✅ SAFE: Empty dependency array stops infinite loop

  // ✅ ENHANCED: Clear all cache function
  const clearAllCache = () => {
    const types = ['baseUrls', 'categories', 'types'];
    types.forEach(type => {
      try {
        localStorage.removeItem(getCacheKey(type));
        localStorage.removeItem(getTimestampKey(type));
      } catch (error) {
        console.error(`Error clearing cache for ${type}:`, error);
      }
    });
    console.log('🗑️ Cleared all contact link cache');
  };

  // ✅ ENHANCED: Function to refresh data with proper cache clearing
  const refreshData = async () => {
    try {
      // ✅ SAFE: Reset refs to allow refresh
      hasInitializedRef.current = false;
      isLoadingRef.current = false;

      setLoading(true);
      setError(null);

      console.log('🔄 Force refreshing all contact link data');

      // ✅ Clear all cache before refreshing
      clearAllCache();

      const [fetchedBaseUrls, fetchedCategories] = await Promise.all([
        handleApiCall(
          () => getBaseUrls(),
          {
            source: 'contact-link-base-urls-refresh',
            retryable: true,
            autoDismiss: true
          }
        ),
        handleApiCall(
          () => getBaseUrlCategories(),
          {
            source: 'contact-link-categories-refresh',
            retryable: true,
            autoDismiss: true
          }
        )
      ]);

      setBaseUrls(fetchedBaseUrls);
      setCategories(fetchedCategories);

      // Cache the new data
      setCachedData('baseUrls', fetchedBaseUrls);
      setCachedData('categories', fetchedCategories);

      // ✅ Fetch types (will be fresh since cache was cleared)
      const fetchedTypes = await fetchTypesForCategories(fetchedCategories);
      setTypes(fetchedTypes);

      // ✅ SAFE: Mark as initialized after successful refresh
      hasInitializedRef.current = true;

    } catch (error) {
      console.error('❌ Error refreshing contact link base URL data:', error);

      // ✅ PRESERVED: Your error handling unchanged
      if (error.message?.includes('validation') ||
        error.message?.includes('permission') ||
        error.message?.includes('session') ||
        error.response?.status === 401 ||
        error.response?.status === 403) {
        addError(error, {
          source: 'contact-link-base-urls-refresh',
          type: 'data-refresh',
          retryable: true,
          autoDismiss: false
        });
      }

      setError(error.message || 'Failed to refresh contact link data');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Function to clear error
  const clearError = () => {
    setError(null);
  };

  return (
    <ContactLinkBaseUrlContext.Provider
      value={{
        categories,
        baseUrls,
        types,
        loading,
        error,
        refreshData,
        clearError,
        clearCache: clearAllCache, // ✅ Export cache clearing
      }}
    >
      {children}
    </ContactLinkBaseUrlContext.Provider>
  );
};

// Custom hook to use the ContactLinkBaseUrlContext
export const useContactLinkBaseUrl = () => {
  const context = useContext(ContactLinkBaseUrlContext);
  if (!context) {
    throw new Error('useContactLinkBaseUrl must be used within a ContactLinkBaseUrlProvider');
  }
  return context;
};